import { JumpToOperator } from './../../../../widgets/jump-to/api/jump-to-operator.api';
import { MapService } from './../../map/map.service';
import { AfterViewInit, Component, ElementRef, Inject, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { EsoService, MappedSourcingDto, Product, ProductsContainerDto, ProductsGroupDto, Sourcing, SourcingDto, SourcingGraphNode } from 'src/app/pages/eso/eso.service';
import { GeneralSetting, Selection, SelectionData, SettingsService } from 'src/app/pages/settings/settings.service';
import { LoadingIndicationService } from 'src/app/services/loading-indication.service';
import { NotificationService } from 'src/app/services/notification.service';
import { Location, DOCUMENT } from "@angular/common";
import { Coordinates } from 'src/app/models/coordinates.model';
import { PopupControl } from 'src/app/models/popup-control.model';
import { NetworkDeepDiveComponent } from 'src/app/pages/eso/network-deep-dive/network-deep-dive.component';
import { MapboxButtonControl } from 'src/app/widgets/mapbox-map/controls/mapbox-button-control';
import * as mapboxgl from 'mapbox-gl';
import { UtilsService } from 'src/app/services/utils.service';
import { ParentStylingService } from 'src/app/services/parent-styling.service';
import { SelectionSetId } from 'src/app/enums/selection-set-id.enum';

@Component({
    selector: 'app-network-deep-dive-map',
    templateUrl: './network-deep-dive-map.component.html',
    styleUrls: ['./network-deep-dive-map.component.scss'],
    host: {
        class: 'flex-grow-1'
    },
    standalone: false
})
export class NetworkDeepDiveMapComponent implements OnInit, AfterViewInit {

	public popupControl: PopupControl = new PopupControl();
	public jumpToOperator: JumpToOperator = new JumpToOperator();

	//#region <General>
	private readonly _productIdQueryParamName = "id";
	//#endregion </General>

	public levelsDropdownOptions: string[] = ["All", "0", "1", "2", "3"];

	private static readonly INPUT_LEVELS_DROPDOWN_TEXT_PREFIX = "Upstream:";
	public inputLevelsDropdownSelectedItemValue: string = this.levelsDropdownOptions[2];
	public inputLevelsDropdownSelectedItem: string = `${NetworkDeepDiveComponent.INPUT_LEVELS_DROPDOWN_TEXT_PREFIX} ${this.inputLevelsDropdownSelectedItemValue}`;

	private static readonly OUTPUT_LEVELS_DROPDOWN_TEXT_PREFIX = "Downstream:";
	public outputLevelsDropdownSelectedItemValue: string = this.levelsDropdownOptions[2];
	public outputLevelsDropdownSelectedItem: string = `${NetworkDeepDiveComponent.OUTPUT_LEVELS_DROPDOWN_TEXT_PREFIX} ${this.outputLevelsDropdownSelectedItemValue}`;

	//#region <Product details data grid.>
	public productDetailsColumns: { columnName: string; caption: string }[] = [];
	public productDetailsData: any[] = [];

	private isFirstTimeLoad = true;

	public initializeProductDetails(esoObjectIds: number[]): void {
		console.log("initializeProductDetails");
		const $this = this;
		$this.productDetailsData = [];

		this._esoService.getEsoNetworkDeepDiveMultipleProductDetails(esoObjectIds).subscribe(
			(response) => {
				console.log("initializeProductDetails -> getEsoNetworkDeepDiveMultipleProductDetails - response:", response);

				$this.productDetailsColumns = response.columns;
				$this.productDetailsData = response.data;
			},
			(error) => {
				console.error(error);

				this._notificationService.error(error);
			}
		);
	}

	public fitTableToPage: boolean = true;

	public changeFitToPageStatus(): void {
		this.fitTableToPage = !this.fitTableToPage;
	}

	//#endregion

	public arcsSourcePointsData: {
		esoObjectIds: any[],
		coordinates: Coordinates
	}[] = [];
	public arcsDestinationPointsData: {
		esoObjectIds: any[],
		coordinates: Coordinates
	}[] = [];
	public arcsSelectedPointData: {
		esoObjectIds: any[],
		coordinates: Coordinates
	} = null;
	public arcsSelfPointsAll: {
		esoObjectIds: any[],
		coordinates: Coordinates
	}[] = [];

	public currentMarkers: {
		marker: mapboxgl.Marker,
		type: string
	}[] = [];
	private initialMarkers: {
		marker: mapboxgl.Marker,
		type: string
	}[] = [];

	public itemIteration = 0;
	public usedCoordinates = [{
		latLong: [[]],
		type: '',
		relatedObjects: []
	}];


	private handleProducts(rootGroup, relatedGroup, isUpstream: boolean) {
		// Stack to keep track of the groups we need to process
		const stack = [{ group: rootGroup, relatedGroup: relatedGroup }];
	
		while (stack.length > 0) {
			// Pop the top group from the stack
			const { group, relatedGroup } = stack.pop();
	
			// If the group has no nested product groups, process it
			if (!group.productsGroups?.length) {
				const groupRelatedObjects = [group.sourcing.mappedSourcing.esoObjectId];
				const relatedGroupRelatedObjects = [relatedGroup.mappedSourcing.esoObjectId];
	
				const sourceCoordinates = new Coordinates(
					isUpstream ? group.sourcing.mappedSourcing.latitude : relatedGroup.mappedSourcing.latitude,
					isUpstream ? group.sourcing.mappedSourcing.longitude : relatedGroup.mappedSourcing.longitude
				);
	
				const destinationCoordinates = new Coordinates(
					isUpstream ? relatedGroup.mappedSourcing.latitude : group.sourcing.mappedSourcing.latitude,
					isUpstream ? relatedGroup.mappedSourcing.longitude : group.sourcing.mappedSourcing.longitude
				);
	
				// Populate source and destination points data
				this.arcsSourcePointsData.push({
					esoObjectIds: isUpstream ? relatedGroupRelatedObjects : groupRelatedObjects,
					coordinates: sourceCoordinates
				});
	
				this.arcsDestinationPointsData.push({
					esoObjectIds: isUpstream ? groupRelatedObjects : relatedGroupRelatedObjects,
					coordinates: destinationCoordinates
				});
	
				// Add arc data
				this.arcsData.push({
					source: sourceCoordinates,
					destination: destinationCoordinates,
					options: null
				});
	
				// Add self-point data
				this.arcsSelfPointsAll.push({
					esoObjectIds: [groupRelatedObjects[0]],
					coordinates: sourceCoordinates
				});
			} else {
				// If the group has nested product groups, push them onto the stack to process them later
				group.productsGroups.forEach(nestedGroup => {
					stack.push({ group: nestedGroup, relatedGroup: group.sourcing });
				});
			}
		}
	}
	
	private handleUpstreamProducts(leftGroup, destination) {
		this.handleProducts(leftGroup, destination, true);
	}
	
	private handleDownstreamProducts(rightGroup, startPoint) {
		this.handleProducts(rightGroup, startPoint, false);
	}
	

	private initMarkers() {
		let $this = this;
		$this.sourcingData.leftProductsGroup.productsGroups.forEach(leftGroup => {
			$this.handleUpstreamProducts(leftGroup, this.sourcingData.selectedProductSourcing);
		});

		$this.sourcingData.rightProductsGroup.productsGroups.forEach(rightGroup => {
			$this.handleDownstreamProducts(rightGroup, this.sourcingData.selectedProductSourcing);
		});

		$this.arcsSelectedPointData = {
			esoObjectIds: [$this.sourcingData.selectedProductSourcing.mappedSourcing.esoObjectId],
			coordinates: new Coordinates(
				$this.sourcingData.selectedProductSourcing.mappedSourcing.latitude,
				$this.sourcingData.selectedProductSourcing.mappedSourcing.longitude
			)
		};

		let settings: GeneralSetting[] = [];

		this._settingsService.GetGeneralSettingsNew().subscribe(res => {
			settings = res.settings.filter(x => x.settingGroupId == 77);

			$this.drawGraphics(settings, $this.arcsSourcePointsData, $this.arcsDestinationPointsData,
				$this.arcsSelectedPointData, $this.arcsData, $this.currentMarkers, $this.arcsSelfPointsAll);
		});
	}

	public arcsData: {
		source: Coordinates,
		destination: Coordinates,

		options: any
	}[] = [];

	private _map: mapboxgl.Map;

	private initializeMap(accessToken) {

		this._map = new mapboxgl.Map(
			{
				accessToken: accessToken,
				container: 'map',
				zoom: 3,
				center: [0, 20],
				style: 'mapbox://styles/mapbox/light-v10',
				renderWorldCopies: false
			}
		);

		const scale = new mapboxgl.ScaleControl(
			{
				maxWidth: 80,
				unit: 'metric'
			}
		);

		const ctrlPoint = new MapboxButtonControl(
			{
				className: "mapbox-gl-draw_point",
				title: "Place a Marker",
				iconClassName: "fa-solid fa-location-dot",
				eventHandler: (evt) => { }
			}
		);

		const ctrlLine = new MapboxButtonControl(
			{
				className: "mapbox-gl-draw_line",
				title: "Draw Line",
				iconClassName: "fa-solid fa-ruler",
				eventHandler: (evt) => { }
			}
		);

		const ctrlPolygon = new MapboxButtonControl(
			{
				className: "mapbox-gl-draw_polygon",
				title: "Draw Polygon",
				iconClassName: "fa-solid fa-draw-polygon",
				eventHandler: (evt) => { }
			}
		);

		/* Add Controls to the Map */
		this._map.addControl(scale);
		this._map.addControl(new mapboxgl.NavigationControl(), "top-left");
		this._map.addControl(new mapboxgl.FullscreenControl());
		this._map.addControl(ctrlPoint, "bottom-left");
		this._map.addControl(ctrlLine, "bottom-right");
		this._map.addControl(ctrlPolygon, "top-right");
	}

	private getArrayOccuranceCount(array, item) {
		let count = 0;
		array.forEach(element => {
			if (element.latLong[0] == item[0] && element.latLong[1] == item[1]) count++;
		});

		return count;
	}

	private checkIfContainsArray(array, item) {
		let isTrue = false;

		array.forEach(element => {
			if (element.latLong[0] == item[0] && element.latLong[1] == item[1]) isTrue = true;
		});

		return isTrue;
	}

	private checkIfMarkerExists(coords, type) {
		let exists = false;
		this.usedCoordinates.forEach(x => {
			if (x.latLong[0] == coords[0] && x.latLong[1] == coords[1] && x.type == type) exists = true;
		});

		return exists;
	}

	private drawGraphics(settings, arcsSourcePointsData, arcsDestinationPointsData, arcsSelectedPointData, arcsData, currentMarkers, allPointsData) {
		const size = settings.find(x => x.name == 'IconsSizePixels').value;
		let width = 0;
		let iconSizeClass = '';
	
		switch (size) {
			case '2XS': width = 10; iconSizeClass = 'fa-2xs'; break;
			case 'XS': width = 12; iconSizeClass = 'fa-xs'; break;
			case 'SM': width = 14; iconSizeClass = 'fa-sm'; break;
			case 'M': width = 16; iconSizeClass = ''; break;
			case 'L': width = 20; iconSizeClass = 'fa-lg'; break;
			case 'XL': width = 24; iconSizeClass = 'fa-xl'; break;
			case '2XL': width = 32; iconSizeClass = 'fa-2xl'; break;
		}
	
		let marker = null;
	
		// 1. Render warehouse icon where the selected item is located
		const warehouseCoordinates = arcsSelectedPointData.coordinates;
		const warehouseEl = document.createElement('div');
		const warehouseIcon = document.createElement('i');
	
		// Set warehouse icon and color
		warehouseIcon.className = settings.find(x => x.name == 'SelectedProductIconClass').value + ` ${iconSizeClass}`;
		warehouseIcon.style.color = settings.find(x => x.name == 'SelectedProductColor').value;
		warehouseEl.appendChild(warehouseIcon);
	
		if (!this.checkIfMarkerExists([warehouseCoordinates.longitude, warehouseCoordinates.latitude], 'selected')) {
			const warehouseMarker = new mapboxgl.Marker(warehouseEl)
				.setLngLat([warehouseCoordinates.longitude, warehouseCoordinates.latitude])
				.addTo(this._map);
	
			this.currentMarkers.push({
				marker: warehouseMarker,
				type: 'selected'
			});
	
			this.usedCoordinates.push({
				latLong: [warehouseCoordinates.longitude, warehouseCoordinates.latitude],
				type: 'selected',
				relatedObjects: arcsSelectedPointData.esoObjectIds
			});
	
			// Attach click event for the warehouse marker
			warehouseMarker.getElement().addEventListener('click', () => {
				this.popupControl.show();
				this._map.flyTo({
					center: new mapboxgl.LngLat(warehouseCoordinates.longitude, warehouseCoordinates.latitude)
				});
				this.popupControl.options.title = "Product Details";
				this.initializeProductDetails(arcsSelectedPointData.esoObjectIds);
			});
		}
	
		// 2. Render other points (pins) except for the selected location
		allPointsData.forEach(item => {
			if (item.coordinates.longitude === warehouseCoordinates.longitude && item.coordinates.latitude === warehouseCoordinates.latitude) {
				return;  // Skip this point as the warehouse is already rendered
			}
	
			const el = document.createElement('div');
			const icon = document.createElement('i');
			// Pin icon logic for all other points
			icon.className = settings.find(x => x.name == 'DefaultPointIcon').value + ` ${iconSizeClass}`;
			icon.style.color = settings.find(x => x.name == 'DefaultPointIconColor').value;
	
			el.appendChild(icon);
	
			if (this.checkIfContainsArray(this.usedCoordinates, [item.coordinates.longitude, item.coordinates.latitude])) {
				const sameElCount = this.getArrayOccuranceCount(this.usedCoordinates, [item.coordinates.longitude, item.coordinates.latitude]);
				el.style.left = (sameElCount * width).toString() + 'px';  // Position relative to other icons
			}
	
			if (!this.checkIfMarkerExists([item.coordinates.longitude, item.coordinates.latitude], 'default')) {
				this.usedCoordinates.push({
					latLong: [item.coordinates.longitude, item.coordinates.latitude],
					type: 'default',
					relatedObjects: item.esoObjectIds
				});
	
				const marker = new mapboxgl.Marker(el)
					.setLngLat([item.coordinates.longitude, item.coordinates.latitude])
					.addTo(this._map);
	
				this.currentMarkers.push({
					marker: marker,
					type: 'default'
				});
	
				// Attach click event for the pin marker
				marker.getElement().addEventListener('click', () => {
					this.popupControl.show();
					this._map.flyTo({
						center: new mapboxgl.LngLat(item.coordinates.longitude, item.coordinates.latitude)
					});
					this.popupControl.options.title = "Product Details";
					this.initializeProductDetails(item.esoObjectIds);
				});
			} else {
				let coords = this.usedCoordinates.find(x => x.latLong[0] == item.coordinates.longitude
					&& x.latLong[1] == item.coordinates.latitude && x.type == 'default');
				coords.relatedObjects.push(...item.esoObjectIds);
			}
		});
	
		// 3. Render source points (to the right of the default point now, but closer)
		arcsSourcePointsData.forEach(item => {
			const el = document.createElement('div');
			const icon = document.createElement('i');
			icon.className = settings.find(x => x.name == 'SourceIconClass').value + ` ${iconSizeClass}`;
			icon.style.color = settings.find(x => x.name == 'SourceColor').value;
	
			el.appendChild(icon);
	
			if (this.checkIfContainsArray(this.usedCoordinates, [item.coordinates.longitude, item.coordinates.latitude])) {
				const sameElCount = this.getArrayOccuranceCount(this.usedCoordinates, [item.coordinates.longitude, item.coordinates.latitude]);
				el.style.left = (sameElCount * width + 10).toString() + 'px';  // Reduced distance to 10px
			}
	
			if (!this.checkIfMarkerExists([item.coordinates.longitude, item.coordinates.latitude], 'source')) {
				this.usedCoordinates.push({
					latLong: [item.coordinates.longitude, item.coordinates.latitude],
					type: 'source',
					relatedObjects: item.esoObjectIds
				});
	
				const marker = new mapboxgl.Marker(el)
					.setLngLat([item.coordinates.longitude, item.coordinates.latitude])
					.addTo(this._map);
	
				this.currentMarkers.push({
					marker: marker,
					type: 'source'
				});
	
				// Attach click event for source marker
				marker.getElement().addEventListener('click', () => {
					this.popupControl.show();
					this._map.flyTo({
						center: new mapboxgl.LngLat(item.coordinates.longitude, item.coordinates.latitude)
					});
					this.popupControl.options.title = "Source Details";
					this.initializeProductDetails(item.esoObjectIds);
				});
			} else {
				let coords = this.usedCoordinates.find(x => x.latLong[0] == item.coordinates.longitude
					&& x.latLong[1] == item.coordinates.latitude && x.type == 'source');
				coords.relatedObjects.push(...item.esoObjectIds);
			}
		});
	
		// 4. Render destination points (to the left of the default point now, but closer)
		arcsDestinationPointsData.forEach(item => {
			const el = document.createElement('div');
			const icon = document.createElement('i');
			icon.className = settings.find(x => x.name == 'DestinationIconClass').value + ` ${iconSizeClass}`;
			icon.style.color = settings.find(x => x.name == 'DestinationColor').value;
	
			el.appendChild(icon);
	
			if (this.checkIfContainsArray(this.usedCoordinates, [item.coordinates.longitude, item.coordinates.latitude])) {
				const sameElCount = this.getArrayOccuranceCount(this.usedCoordinates, [item.coordinates.longitude, item.coordinates.latitude]);
				el.style.left = '-' + (sameElCount * width + 10).toString() + 'px';  // Reduced distance to 10px
			}
	
			if (!this.checkIfMarkerExists([item.coordinates.longitude, item.coordinates.latitude], 'dest')) {
				this.usedCoordinates.push({
					latLong: [item.coordinates.longitude, item.coordinates.latitude],
					type: 'dest',
					relatedObjects: item.esoObjectIds
				});
	
				const marker = new mapboxgl.Marker(el)
					.setLngLat([item.coordinates.longitude, item.coordinates.latitude])
					.addTo(this._map);
	
				this.currentMarkers.push({
					marker: marker,
					type: 'dest'
				});
	
				// Attach click event for destination marker
				marker.getElement().addEventListener('click', () => {
					this.popupControl.show();
					this._map.flyTo({
						center: new mapboxgl.LngLat(item.coordinates.longitude, item.coordinates.latitude)
					});
					this.popupControl.options.title = "Destination Details";
					this.initializeProductDetails(item.esoObjectIds);
				});
			} else {
				let coords = this.usedCoordinates.find(x => x.latLong[0] == item.coordinates.longitude
					&& x.latLong[1] == item.coordinates.latitude && x.type == 'dest');
				coords.relatedObjects.push(...item.esoObjectIds);
			}
		});
	
		// 5. Draw arcs between source and destination points with direction arrows
		arcsData.forEach(arc => {
			this._map.addSource('route-' + this.itemIteration.toString(), {
				'type': 'geojson',
				'data': {
					'type': 'Feature',
					'properties': {},  // Ensure 'properties' is present
					'geometry': {
						'type': 'LineString',
						'coordinates': [
							[arc.source.longitude, arc.source.latitude], 
							[arc.destination.longitude, arc.destination.latitude]
						]
					}
				}
			});
	
			this._map.addLayer({
				'id': 'lines-' + this.itemIteration.toString(),
				'type': 'line',
				'source': 'route-' + this.itemIteration.toString(),
				'layout': {
					'line-join': 'round',
					'line-cap': 'round'
				},
				'paint': {
					'line-color': '#888',
					'line-width': 3
				}
			});
	
			// Load direction icon (ensure 'directionIcon' is correctly loaded)
			if (!this._map.hasImage('directionIcon')) {
				let directionImage = new Image(50, 50);
				directionImage.src = './assets/icons/arrow-up-solid-full.svg';  // Ensure this path points to a valid arrow icon
				directionImage.onload = () => {
					this._map.addImage('directionIcon', directionImage);
				};
			}
	
			// Add direction arrows on the arcs
			this._map.addLayer({
				'id': 'directions-' + this.itemIteration.toString(),
				'type': 'symbol',
				'source': 'route-' + this.itemIteration.toString(),
				'layout': {
					'symbol-placement': 'line',
					'icon-image': 'directionIcon',
					'icon-rotate': 90,
					'icon-rotation-alignment': 'map',
					'icon-allow-overlap': true,
					'icon-size': 0.6,
					'symbol-spacing': 400  // Adjust spacing for arrow placement
				}
			});
	
			this.itemIteration++;
		});
	
		// 6. Fly to the selected point on the map
		this._map.flyTo({
			center: new mapboxgl.LngLat(arcsSelectedPointData.coordinates.longitude, arcsSelectedPointData.coordinates.latitude)
		});
	}
	
	
						
					

	public selectedProduct: Product = new Product();

	public selectProduct(productId: string) {
		console.log("selectProduct - productId:", productId);
		

		// Create a new Temp Product until selected one is initialized, because many methods use this.selectedProduct to access code property.
		this.selectedProduct = Object.assign(new Product(), { code: productId });

		const url = `eso/network/deep-dive-map?${this._productIdQueryParamName}=${UtilsService.getURLHelper(this.selectedProduct.code)}`;
		this._location.replaceState(url);

		this.getSelectedProductData();

	}

	selectProductWithReload(productId: string) {
		this.cleanMap();
		this.arcsData = [];
		this.arcsSourcePointsData = [];
		this.arcsDestinationPointsData = [];
		this.arcsSelfPointsAll = [];

		this.selectedProduct = Object.assign(new Product(), { code: productId });

		const url = `eso/network/deep-dive-map?${this._productIdQueryParamName}=${this.selectedProduct.code}`;
		this._location.replaceState(url);

		this.getSelectedProductData();

		window.location.reload();
	}

	public topRows: number | null = null;

	public orderByColumnName: string = 'ProductId';
	public orderByColumnNameItems: Selection[];

	public orderByDirection: 'ASC' | 'DESC' = 'DESC';

	public sourcingData: ProductsContainerDto;

	private populateProductsGroupRecursively(
		data: SourcingGraphNode[],
		productsGroup: ProductsGroupDto,
		parentEsoObjectId: number,
		level: number,
		increment: number
	): ProductsGroupDto {

		const levelsData: SourcingGraphNode[] = data.filter(
			(sourcingGraphNode) => sourcingGraphNode.level === level && sourcingGraphNode.parentEsoObjectId === parentEsoObjectId
		);

		productsGroup.productsGroups = [];

		for (let a = 0; a < levelsData.length; a++) {
			const levelData: SourcingGraphNode = levelsData[a];
			const levelProductsGroup: ProductsGroupDto = new ProductsGroupDto();

			levelProductsGroup.sourcing = new SourcingDto(
				Object.assign(
					new Sourcing(),
					{
						outputEsoObjectId: level < 0 ? levelData.parentEsoObjectId : levelData.currentEsoObjectId,
						inputEsoObjectId: level < 0 ? levelData.currentEsoObjectId : levelData.parentEsoObjectId,
						inputQtyPerOutput: levelData.inputQtyPerOutput,
						esoObjectName: levelData.esoObjectName,
						ddMrpStockStatus: levelData.dDMrpStockStatus,
					}
				),
				Object.assign(
					new MappedSourcingDto(),
					levelData,
					{
						esoNetworkInfo: levelData.text
					}
				)
			);

			productsGroup.productsGroups.push(
				this.populateProductsGroupRecursively(
					data,
					levelProductsGroup,
					levelData.currentEsoObjectId,
					level + increment,
					increment
				)
			);
		}

		return productsGroup;
	}

	public getSelectedProductData(): void {
		const $this = this;

		let inputLevels = +this.inputLevelsDropdownSelectedItemValue;
		let outputLevels = +this.outputLevelsDropdownSelectedItemValue

		this._esoService.getEsoSourcingGraphData(
			{
				productId: this.selectedProduct.code,
				topRows: isNaN(this.topRows) ? null : this.topRows,
				upstreamLevel: isNaN(inputLevels) ? null : inputLevels,
				downStreamLevel: isNaN(outputLevels) ? null : outputLevels,
				orderByColumnName: this.orderByColumnName,
				orderByDirection: this.orderByDirection
			},
			{
				showLoadingIndicator: true,
			}
		).subscribe(
			{
				next: (response) => {


					$this.sourcingData = new ProductsContainerDto();

					const zeroLevelData: SourcingGraphNode = response.find((item) => item.level === 0);

					$this.sourcingData.selectedProductSourcing = new SourcingDto(
						null,
						Object.assign(
							new MappedSourcingDto(),
							zeroLevelData,
							{
								esoNetworkInfo: zeroLevelData.text
							}
						)
					);

					$this.sourcingData.leftProductsGroup = this.populateProductsGroupRecursively(response, new ProductsGroupDto(), zeroLevelData.currentEsoObjectId, -1, -1);
					$this.sourcingData.rightProductsGroup = this.populateProductsGroupRecursively(response, new ProductsGroupDto(), zeroLevelData.currentEsoObjectId, 1, 1);

					$this.initMarkers();
				}
			}
		);
	}

	public productsAutocompleteDataSource: string[] = [];

	public allProducts: Product[] = [];

	public autocompleteInitialized: boolean;

	public initializeAutocompleteList(): void {
		console.log("initializeAutocompleteList -> allProducts:", this.allProducts);

		let source = this.allProducts.map((product) => product.code);

		this.productsAutocompleteDataSource = source;

		this.autocompleteInitialized = true;

		console.log("initializeAutocompleteList -> productsAutocompleteDataSource:", this.productsAutocompleteDataSource);
	}

	private initializeAutocompleteData(): void {
		const $this = this;

		$this._activatedRoute.queryParamMap.subscribe(
			(queryParams) => {
				let productId = queryParams.get($this._productIdQueryParamName);

				console.warn("TEST", queryParams);

				if (productId) {
					console.log("initializeSelectedProduct -> queryParamMap - productId:", productId);

					$this.selectProduct(productId);
				}

				$this._esoService.getSourcingAutocompleteData().subscribe(
					(response) => {
						console.log("initializeAutocompleteData -> getSourcingAutocompleteData - response:", response);

						$this.allProducts = [];

						for (let a = 0; a < response.length; a++) {
							$this.allProducts.push(Object.assign(new Product(), { code: response[a] }));
						}

						$this.initializeAutocompleteList();

						if (!productId) {
							productId = $this.allProducts[0].code;

							$this.selectProduct(productId);
						}

						console.log("initializeAutocompleteData -> getSourcingAutocompleteData - productId:", productId);
					}
				);
			}
		);
	}

	public onAutocompleteItemClick(event): void {
		this.selectProduct(this.selectedProduct.code);
	}

	private cleanMap() {
		for (let i = 0; i < this.itemIteration; i++) {
			this._map.removeLayer('directions-' + i.toString());
			this._map.removeLayer('lines-' + i.toString());
			this._map.removeSource('route-' + i.toString());
		}

		this.currentMarkers.forEach(x => {
			x.marker.remove();
		});

		this.currentMarkers = [];
		this.usedCoordinates = [];
		this.itemIteration = 0;
	}

	public onInputLevelsDropdownClicked(event) {
		const selectedItem = event.itemData;
		if (+selectedItem < +this.inputLevelsDropdownSelectedItemValue) {
			this.arcsData = [];
			this.arcsSourcePointsData = [];
			this.arcsDestinationPointsData = [];
			this.cleanMap();
		}

		this.inputLevelsDropdownSelectedItemValue = selectedItem;
		this.inputLevelsDropdownSelectedItem = `${NetworkDeepDiveComponent.INPUT_LEVELS_DROPDOWN_TEXT_PREFIX} ${this.inputLevelsDropdownSelectedItemValue}`;

		this.selectProduct(this.selectedProduct.code);
	}

	public onOutputLevelsDropdownClicked(event) {
		const selectedItem = event.itemData;
		if (+selectedItem < +this.outputLevelsDropdownSelectedItemValue) {
			this.arcsData = [];
			this.arcsSourcePointsData = [];
			this.arcsDestinationPointsData = [];
			this.cleanMap();
		}

		this.outputLevelsDropdownSelectedItemValue = selectedItem;
		this.outputLevelsDropdownSelectedItem = `${NetworkDeepDiveComponent.OUTPUT_LEVELS_DROPDOWN_TEXT_PREFIX} ${this.outputLevelsDropdownSelectedItemValue}`;

		this.selectProduct(this.selectedProduct.code);
	}

	public onMapContainerResized(): void {

		this._map.resize();
	}

	constructor(
		private _notificationService: NotificationService,
		private _loadingIndicationService: LoadingIndicationService,
		private _settingsService: SettingsService,

		@Inject(DOCUMENT) private document: Document,
		private _mapService: MapService,
		private _esoService: EsoService,

		private _location: Location,
		private _activatedRoute: ActivatedRoute,
		private _elementRef: ElementRef,
		private _parentStylingService: ParentStylingService,
	) { }

	getAccessToken() {
		console.log(this._settingsService.MapAccess());
	}

	ngOnInit() {
		this._settingsService.MapAccess().then(value => {
			this.initializeMap(value);
		});

		this.initializeAutocompleteData();

		this._settingsService.getSelections(
			SelectionSetId.EsoNetworkDeepDiveOrderByColumnNameItems,
			{
				showLoadingIndicator: true
			}
		).subscribe(
			{
				next: (selectionData: SelectionData) => {
					this.orderByColumnNameItems = selectionData.selections.map(
						(selection: Selection) => {
							if (!selection.caption) {
								selection.caption = `${selection.selectionName}`;// [Caption Missing]`;
							}

							return selection;
						}
					);
				}
			}
		);
	}

	ngAfterViewInit() {
		// For the map to display we need parent of the host component to be d-flex but we don't want that on every page, so we have to add it here.
		this._parentStylingService.addClassToParentUntilNavigationEnd(this._elementRef.nativeElement, 'd-flex');
	}
}
